import { useNavigate, useSearch } from '@tanstack/react-router'

import Button from '@ingka/button'
import Pill from '@ingka/pill'
import adjustableBackrest from '@ingka/ssr-icon/paths/adjustable-backrest'
import cross from '@ingka/ssr-icon/paths/cross'
import pencil from '@ingka/ssr-icon/paths/pencil'
import sofaBed from '@ingka/ssr-icon/paths/sofa-bed'
import sofaStorage from '@ingka/ssr-icon/paths/sofa-storage'
import washableCover from '@ingka/ssr-icon/paths/washable-cover'
import Text from '@ingka/text'

import { dataTestIds } from '@/constants/selectors'
import { startGalleryFilterSchemaDefaultValues } from '@/features/start-gallery/filter'
import { getFiltersToShow, useRangeGalleryData } from '@/features/start-gallery/hooks/use-range-gallery-data'
import { useInsights } from '@/hooks/use-insights'
import { useRangeData } from '@/hooks/use-range-data'
import { useTranslate } from '@/hooks/use-translate'

import { clearButton, filterContainer, filterPillContainer } from './filter.module.css'
import { SizeFilter } from './size-filter'

export function Filter() {
  const { hasRemovableCover, hasSofabed, hasStorage, isModular, width, hasRecliningBackrest } = useSearch({
    from: '/$country/$language/$mode/',
  })
  const navigate = useNavigate({ from: '/$country/$language/$mode/' })
  const rangeGalleryData = useRangeGalleryData()
  const { data } = useRangeData()
  const filtersToShow = getFiltersToShow(rangeGalleryData)
  const doesMarketHaveASofaWithRecliner = Object.values(data).some((item) => item.hasRecliningBackrest)
  const shouldShowClearFilter =
    hasRemovableCover || hasRecliningBackrest || hasSofabed || hasStorage || isModular || width !== 'all'
  const { translateString } = useTranslate()
  const { sendCustomEvent } = useInsights()
  const ariaLabelFilterGroup = `${translateString('startpage.filter.title')} ${translateString('startpage.intro.title').toLowerCase()}`

  return (
    <div className={filterContainer}>
      <Text tagName="h2" textStyle="Heading.M">
        {translateString('startpage.filter.title')}
      </Text>
      <fieldset aria-label={ariaLabelFilterGroup} className={filterPillContainer}>
        <Pill
          data-testid={dataTestIds.landing.removableCoverPill}
          disabled={!filtersToShow.shouldShowRemovableCover}
          label={translateString('common.removable_cover')}
          leadingIcon={washableCover}
          onClick={() => {
            void navigate({
              search: (prev) => ({ ...prev, hasRemovableCover: !prev.hasRemovableCover }),
            })
            void sendCustomEvent('filter_removable_cover', { payload: { hasRemovableCover: !hasRemovableCover } })
          }}
          selected={hasRemovableCover}
          size="small"
        />
        <Pill
          data-testid={dataTestIds.landing.sofaBedPill}
          disabled={!filtersToShow.shouldShowSofaBed}
          label={translateString('startpage.filter.sofa_bed')}
          leadingIcon={sofaBed}
          onClick={() => {
            void navigate({
              search: (prev) => ({ ...prev, hasSofabed: !prev.hasSofabed }),
            })
            void sendCustomEvent('filter_sofa_bed', { payload: { hasSofabed: !hasSofabed } })
          }}
          selected={hasSofabed}
          size="small"
        />
        <Pill
          data-testid={dataTestIds.landing.storagePill}
          disabled={!filtersToShow.shouldShowStorage}
          label={translateString('startpage.filter.storage')}
          leadingIcon={sofaStorage}
          onClick={() => {
            void navigate({
              search: (prev) => ({ ...prev, hasStorage: !prev.hasStorage }),
            })
            void sendCustomEvent('filter_storage', { payload: { hasStorage: !hasStorage } })
          }}
          selected={hasStorage}
          size="small"
        />
        {doesMarketHaveASofaWithRecliner && (
          <Pill
            data-testid={dataTestIds.landing.recliningBackrestPill}
            disabled={!filtersToShow.shouldShowRecliners}
            label={translateString('startpage.card.recliner')}
            leadingIcon={adjustableBackrest}
            onClick={() => {
              void navigate({
                search: (prev) => ({ ...prev, hasRecliningBackrest: !prev.hasRecliningBackrest }),
              })
              void sendCustomEvent('filter_recline', { payload: { hasRecliningBackrest: !hasRecliningBackrest } })
            }}
            selected={hasRecliningBackrest}
            size="small"
          />
        )}
        <Pill
          data-testid={dataTestIds.landing.modularPill}
          disabled={!filtersToShow.shouldShowIsModular}
          label={translateString('startpage.filter.modular')}
          leadingIcon={pencil}
          onClick={() => {
            void navigate({
              search: (prev) => ({ ...prev, isModular: !prev.isModular }),
            })
            void sendCustomEvent('filter_modular', { payload: { isModular: !isModular } })
          }}
          selected={isModular}
          size="small"
        />
        <SizeFilter />
        {shouldShowClearFilter && (
          <Button
            id={`clear-all-filters`}
            className={clearButton}
            data-testid={dataTestIds.landing.clearFilterButton}
            onClick={() => {
              void navigate({
                search: (prev) => ({ ...prev, ...startGalleryFilterSchemaDefaultValues }),
              })
            }}
            size="small"
            ssrIcon={cross}
            type="tertiary"
          >
            {translateString('startpage.filter.clear_all_filters')}
          </Button>
        )}
      </fieldset>
    </div>
  )
}
